import React from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CircularProgress } from '@mui/material'
import deleteIcon from '../../../../assets/deleteIcon.svg'
import { type KpiKeyBubble, type KpiKey } from '../../redux/changeKpiType'
import BubbleChart from './BubbleChart'
import { isClickableKpiKey } from '../../redux/changeKpiType'
import { getKpiInfo } from './kpiInfo'
import type dayjs from 'dayjs'
import { type BubbleKpiState } from '../../types'
import { type DonutSeriesData } from './DonutChart'
import AuthError from '../../../../components/ErrorComponents/AuthError'
import { parseDateInAOHstandard } from '../../../../customHooks/parseDate'

interface Props {
  kpiName: KpiKeyBubble
  retryHandler: () => void
  handleKpiRemove: (kpi: KpiKey) => void
  buildDonutChartData: (kpiName: KpiKeyBubble, kpiData: DonutSeriesData, setKpiData: React.Dispatch<React.SetStateAction<DonutSeriesData>>, kpiDataState: BubbleKpiState) => void
  dateRange: dayjs.Dayjs[]
  setDateRange: React.Dispatch<React.SetStateAction<dayjs.Dayjs[]>>
  state: BubbleKpiState
}

function SpreadFactorKPI (props: Props): JSX.Element {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [count, setCount] = React.useState<number>(0)

  const kpiDataTemp: DonutSeriesData = {
    name: [],
    data: [],
    color: []
  }
  const [kpiData, setKpiData] = React.useState<DonutSeriesData>(kpiDataTemp)

  React.useEffect(() => {
    props.buildDonutChartData(props.kpiName, kpiData, setKpiData, props.state)
    if (props.state.data !== undefined) {
      setCount(props.state.data.totalCount)
    }
  }, [props.state])

  const kpiClickHandler = (kpiName: KpiKey): void => {
    if (isClickableKpiKey(kpiName)) {
      dispatch({ type: 'kpiTypeSlice/changeKpiType', payload: kpiName })
      navigate('/kpi-details')
    }
  }

  function lastUpdatedTime (): string {
    const timestamp = props.state.data?.CalculatedAt !== undefined && props.state.data?.CalculatedAt !== null ? new Date(props.state.data?.CalculatedAt) : ''
    const dayJsUtcTime = parseDateInAOHstandard(timestamp)

    const lastUpdated = `Last updated: ${dayJsUtcTime}`

    return lastUpdated
  }

  const handleWithFailure = (): JSX.Element => {
    if (props.state.isLoading) {
      return (
        <div style={{ width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}><CircularProgress /></div>
      )
    } else if (props.state.httpStatus === 401) {
      return (
        <div className='authErrorThirtyDaysTend daystrend401'><AuthError errorMessage="Unauthorized"/></div>
      )
    } else if (props.state.httpStatus === 403) {
      return (<div className='authErrorThirtyDaysTend daystrend401'><AuthError errorMessage="accessForbidden"/></div>)
    } else if (props.state.httpStatus === 200 && props.state.data === undefined) {
      return (
        <div className='authErrorThirtyDaysTend daystrend401'><AuthError errorMessage="NoDataPresent"/></div>
      )
    } else if (props.state.data !== undefined && props.state.httpStatus === 200) {
      return (
        <BubbleChart data={kpiData} height={window.innerWidth >= 1896 ? 350 : 280} />
      )
    } else {
      return (
        <div className='authErrorThirtyDaysTend daystrend401'><AuthError errorMessage="cannotFetch" retry={props.retryHandler}/></div>
      )
    }
  }

  return (
    <>
      <Card className='kpi-card' sx={{ height: window.innerWidth >= 1896 ? 457 : 382, borderRadius: '10px', backgroundColor: '#313030', color: '#f0f0f0', overflow: 'hidden', position: 'relative', '& .MuiCardContent-root': { overflow: 'hidden' } }}>
        <div style={{ overflow: 'visible', position: 'relative' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <div onClick={() => { kpiClickHandler(props.kpiName) }} style={{ cursor: 'pointer', fontSize: window.innerWidth > 1300 ? '16px' : '13px', padding: '15px', paddingBottom: '0px', letterSpacing: '1px', textTransform: 'uppercase', color: isClickableKpiKey(props.kpiName) ? '#64C3FF' : '#F0F0F0', position: 'relative', overflow: 'visible' }}>{props.kpiName} ({count}) {getKpiInfo(props.kpiName)}</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <img src={deleteIcon} onClick={() => { props.handleKpiRemove(props.kpiName) }} style={{ marginRight: 15, cursor: 'pointer', marginLeft: 5 }} alt='delete'/>
            </div>
          </div>
          <div style={{ padding: '0px 1px 15px 15px', fontSize: 14, fontWeight: 300 }}>
            <span>
              {props.state.data?.CalculatedAt !== null && props.state.data?.CalculatedAt !== undefined && lastUpdatedTime()}
            </span>
          </div>
        </div>
        <CardContent sx={{ padding: '12px 8px', paddingBottom: '8px', overflow: 'hidden', position: 'relative' }}>
          <div style={{ overflowX: 'auto', overflowY: 'hidden', width: '100%', position: 'relative', paddingBottom: 10 }}>
            <div style={{ width: 600, margin: '0 auto' }}>
              {handleWithFailure()}
            </div>
          </div>
        </CardContent>
      </Card>
    </>
  )
}

export default SpreadFactorKPI
